import { CalendarDate, CalendarDateTime, type DateValue, getLocalTimeZone, today, ZonedDateTime } from '@internationalized/date';
import {
	parseISO,
	startOfWeek,
	format,
	addDays,
	parse,
	isSameDay as dfIsSameDay,
	startOfTomorrow,
	startOfToday,
	startOfDay,
	addHours,
	startOfMonth,
	differenceInDays,
	differenceInMinutes,
	differenceInYears,
	endOfMonth,
	isPast,
	endOfWeek,
	isEqual,
	getDay,
	endOfDay,
	formatISO,
	isSameWeek,
	isSameMonth,
	formatDistanceToNow,
	differenceInCalendarDays,
	getWeeksInMonth,
	getWeekOfMonth,
	getWeek,
	addWeeks,
	getMonth,
	getDate
} from 'date-fns';
import type { Granularity } from '@react-types/datepicker';

export const dateAndTimeFormats = {
	header: 'eeee, MMMM d hh:mm a', //Tuesday, March 26 10:05 AM
	shortDateAndTime: 'MM/dd/yyyy hh:mm a', //03/26/2024 10:05 AM,
	shortDateTwoYearAndTime: 'MM/dd/yy hh:mm a', //03/26/20 10:05 AM
	shortDate: 'MM/dd/yyyy', //03/26/2024
	shortDateTwoYear: 'MM/dd/yy', //03/26/24
	longDate: 'MMM dd, yyyy', //Mar 26, 2024
	shortTime: 'hh:mm a', //10:04 AM
	systemDate: 'yyyy-MM-dd', //2024-03-26
	monthAndDay: 'LLLL dd', // January 26
	monthAndYear: 'dd, yyyy', // 26, 2024
	fullMonthDayAndYear: 'LLLL dd, yyyy', //March 26, 2024'
	dayOfWeekAndDate: 'eeee MM/dd'
};

export function getMonthYear(date: Date) {
	const month = String(date.getMonth() + 1).padStart(2, '0'); // Get month (0-indexed) and pad with leading zero
	const year = date.getFullYear();
	return `${month}/${year}`;
}

export function getToday() {
	return today(getLocalTimeZone());
}

export function getDaysUntilToday(date: Date) {
	const today = startOfDay(new Date());
	const targetDate = startOfDay(date);
	return differenceInCalendarDays(targetDate, today);
}

export function getDaysRemainingInWeek(weekStartsOn: number, date: Date) {
	const dayOfWeek = getDay(date);
	return weekStartsOn <= dayOfWeek ? 7 - dayOfWeek - weekStartsOn : weekStartsOn - dayOfWeek;
}

export function getPreviousOccurenceOf(currentDate: Date, dayOfWeek: number, useCurrentDateIfMatches: boolean = false) {
	const currentDayOfWeek = getDay(currentDate);
	if (dayOfWeek === currentDayOfWeek) {
		return !useCurrentDateIfMatches ? addDays(currentDate, -7) : currentDate;
	}
	const diff = currentDayOfWeek - dayOfWeek;
	if (diff > 0) {
		return addDays(currentDate, -diff);
	}
	return addDays(currentDate, -(diff + 7));
}

export function getMSTilMidnight(): number {
	const now = new Date();
	const midnight = new Date(now);
	midnight.setHours(24, 0, 0, 0);
	return midnight.getTime() - now.getTime();
}

export function getAge(dateOfBirth: Date, asOf: Date = new Date()): number {
	let age = asOf.getFullYear() - dateOfBirth.getFullYear();
	const monthDiff = asOf.getMonth() - dateOfBirth.getMonth();

	if (monthDiff < 0 || (monthDiff === 0 && asOf.getDate() < dateOfBirth.getDate())) {
		age--;
	}

	return age;
}

export function isBetweenOrEqualToByDay(startDate: Date, endDate: Date, compareDate: Date): boolean {
	return differenceInDays(compareDate, startDate) >= 0 && differenceInDays(compareDate, endDate) <= 0;
}

export function formatToShortDateOrNull(date: Date | null | undefined, nullText?: string): string | null {
	return date ? format(date, dateAndTimeFormats.shortDate) : nullText ?? null;
}

export function formatToShortDate(date: Date): string {
	return format(date, dateAndTimeFormats.shortDate);
}

export function formatToShortTime(date: Date) {
	return format(date, dateAndTimeFormats.shortTime);
}

export function formatToShortDateAndTimeOrNull(date: Date | null | undefined, nullText?: string): string | null {
	return date ? format(date, dateAndTimeFormats.shortDateAndTime) : nullText ?? null;
}

export function formatToShortDateAndTime(date: Date): string {
	return format(date, dateAndTimeFormats.shortDateAndTime);
}

export function formatToShortDateTwoYearOrNull(date: Date | null | undefined, nullText?: string): string | null {
	return date ? format(date, dateAndTimeFormats.shortDateTwoYear) : nullText ?? null;
}

export function formatToDateTwoYearAndTimeOrNull(date: Date | null | undefined, nullText?: string) {
	return date ? format(date, dateAndTimeFormats.shortDateTwoYearAndTime) : nullText ?? null;
}

export function formatTimespanForDisplay(startTime: Date, endTime?: Date) {
	const formattedTimeDisplay = format(startTime, ' hh:mm a');
	if (endTime) {
		return `${formattedTimeDisplay} - ${format(endTime, 'hh:mm a')}`;
	}
	return formattedTimeDisplay;
}

export function convertMonthYearToDate(date: string) {
	const value = date.replace(/[^0-9]/g, '');
	const month = parseInt(value.slice(0, 2), 10);
	const year = parseInt(value.slice(2), 10);
	return new Date(year, month - 1, 1);
}

export function convertToDateFieldValue(value: Date, granularity?: Granularity): CalendarDateTime | CalendarDate {
	if (!granularity || granularity === 'day') {
		return new CalendarDate(value.getFullYear(), value.getMonth() + 1, value.getDate());
	} else if (granularity === 'hour') {
		return new CalendarDateTime(value.getFullYear(), value.getMonth() + 1, value.getDate(), value.getHours());
	} else if (granularity === 'minute') {
		return new CalendarDateTime(value.getFullYear(), value.getMonth() + 1, value.getDate(), value.getHours(), value.getMinutes());
	}
	return new CalendarDateTime(value.getFullYear(), value.getMonth() + 1, value.getDate(), value.getHours(), value.getMinutes(), value.getSeconds());
}

export function convertFromDateFieldValue(value: CalendarDate | CalendarDateTime | ZonedDateTime): Date {
	const date = new Date(0);
	if (value instanceof CalendarDateTime) {
		date.setFullYear(value.year, value.month - 1, value.day);
		date.setHours(value.hour, value.minute, value.second, 0);
	} else if (value instanceof ZonedDateTime) {
		const localDate = value.toDate();
		date.setFullYear(localDate.getFullYear(), localDate.getMonth(), localDate.getDate());
		date.setHours(localDate.getHours(), localDate.getMinutes(), localDate.getSeconds(), 0);
	} else {
		date.setFullYear(value.year, value.month - 1, value.day);
		date.setHours(0, 0, 0, 0);
	}
	return date;
}

export function convertToDateValue(value: Date | string): DateValue {
	if (typeof value === 'string') {
		const parsedDate = parseISO(value);
		if (isNaN(parsedDate.getTime())) {
			throw new Error(`Invalid date string: ${value}`);
		}
		return convertToDateFieldValue(parsedDate);
	}
	return convertToDateFieldValue(value, 'day');
}

export function convertFromDateValue(value: DateValue): Date {
	return value.toDate(getLocalTimeZone());
}

export function convertHoursToDays(hours: number | null | undefined): number {
	if (hours === null || hours === undefined) {
		return 0;
	}
	return Math.floor(hours / 24);
}

export function convertClientDateToServerDate(dateTime: Date): string {
	const year = dateTime.getFullYear();
	const month = String(dateTime.getMonth() + 1).padStart(2, '0');
	const day = String(dateTime.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

export function convertUrlDateToClientDate(urlDate: string): Date {
	const [year, month, day] = urlDate.split('-').map(Number);
	if (!year || isNaN(year) || !month || isNaN(month) || !day || isNaN(day)) {
		throw new Error(`Invalid date string: ${urlDate}`);
	}

	return new Date(year, month - 1, day);
}

export function convertClientDateTimeToServerDateTime(dateTime: Date): string {
	const year = dateTime.getFullYear();
	const month = String(dateTime.getMonth() + 1).padStart(2, '0');
	const day = String(dateTime.getDate()).padStart(2, '0');
	const hours = String(dateTime.getHours()).padStart(2, '0');
	const minutes = String(dateTime.getMinutes()).padStart(2, '0');
	const seconds = String(dateTime.getSeconds()).padStart(2, '0');
	return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

export {
	parseISO,
	addHours,
	addDays,
	startOfTomorrow,
	format,
	startOfToday,
	startOfMonth,
	differenceInMinutes,
	isPast,
	startOfWeek,
	endOfWeek,
	parse,
	isEqual,
	getDay,
	endOfDay,
	formatISO,
	dfIsSameDay,
	isSameWeek,
	isSameMonth,
	differenceInYears,
	formatDistanceToNow,
	differenceInDays,
	differenceInCalendarDays,
	startOfDay,
	getWeeksInMonth,
	getWeekOfMonth,
	getWeek,
	addWeeks,
	getMonth,
	getDate,
	endOfMonth
};
